/**
 * Vendor onboarding shapes mirrored from backend zod validators.
 * Kept hand-written for now; future task could generate from OpenAPI.
 */

export type VendorModuleType =
  | 'VET'
  | 'WALKER'
  | 'TRAINER'
  | 'GROOMER'
  | 'CAFE'
  | 'FOOD'
  | 'RESTAURANT'
  | 'PHARMACY'
  | 'LAB'
  | 'CLINIC'
  | 'CREMATION'
  | 'ADOPTION';

export type OnboardingStep =
  | 'STARTED'
  | 'BASIC_INFO_DONE'
  | 'ADDRESS_DONE'
  | 'PAYOUT_DONE'
  | 'TERMS_DONE'
  | 'SUBMITTED';

export type KycStatus =
  | 'PENDING'
  | 'DOCUMENTS_SUBMITTED'
  | 'VERIFIED'
  | 'REJECTED'
  | 'SUSPENDED';

export type KycDocumentType =
  | 'FSSAI_LICENSE'
  | 'DRUG_LICENSE'
  | 'VET_REGISTRATION_CERTIFICATE'
  | 'DEGREE_CERTIFICATE'
  | 'TRAINER_CERTIFICATE'
  | 'BUSINESS_REGISTRATION'
  | 'GST_CERTIFICATE'
  | 'OWNER_ID_PROOF'
  | 'OWNER_ADDRESS_PROOF'
  | 'ORGANIZATION_REGISTRATION'
  | 'SELFIE'
  | 'OTHER';

export type VendorPhotoType =
  | 'LOGO'
  | 'COVER'
  /**
   * A photo of the PERSON, distinct from the business's logo or storefront.
   *
   * Deliberately its own type rather than reusing STAFF: for a walker or trainer this is the
   * face a customer opens the door to, and the one a KYC reviewer compares against the ID
   * document. Lumping it in with general staff photos loses both jobs.
   */
  | 'PROFILE_PHOTO'
  | 'STORE_FRONT'
  | 'COUNTER'
  | 'STORAGE'
  | 'GALLERY'
  | 'INTERIOR'
  | 'EXTERIOR'
  | 'STAFF';

/**
 * Modules a vendor can sign up to run — what the first onboarding screen offers.
 *
 * No ADOPTION: adoption is peer-to-peer, so pets are listed by their owners from
 * the owner app and there is nothing for a vendor to operate. The server refuses
 * it too, so an old client cannot sneak it through. `VendorModuleType` still
 * includes it because existing profiles and drafts do.
 */
export const ALL_MODULE_TYPES: VendorModuleType[] = [
  'VET',
  'WALKER',
  'TRAINER',
  'GROOMER',
  'CAFE',
  'FOOD',
  'RESTAURANT',
  'PHARMACY',
  'LAB',
  'CLINIC',
  'CREMATION',
];

export const KYC_DOCUMENT_TYPES: KycDocumentType[] = [
  'FSSAI_LICENSE',
  'DRUG_LICENSE',
  'VET_REGISTRATION_CERTIFICATE',
  'DEGREE_CERTIFICATE',
  'TRAINER_CERTIFICATE',
  'BUSINESS_REGISTRATION',
  'GST_CERTIFICATE',
  'OWNER_ID_PROOF',
  'OWNER_ADDRESS_PROOF',
  'ORGANIZATION_REGISTRATION',
  'SELFIE',
  'OTHER',
];

export const PHOTO_TYPES: VendorPhotoType[] = [
  'LOGO',
  'COVER',
  'PROFILE_PHOTO',
  'STORE_FRONT',
  'INTERIOR',
  'EXTERIOR',
  'COUNTER',
  'STORAGE',
  'GALLERY',
  'STAFF',
];

/**
 * Human wording for the photo-type picker.
 *
 * The picker used to render the raw enum, so a vendor chose between `STORE_FRONT` and
 * `PROFILE_PHOTO` in shouting snake case. Falling back to the enum for anything missing here
 * keeps a newly added type visible rather than blank.
 */
export const PHOTO_TYPE_LABELS: Record<VendorPhotoType, string> = {
  LOGO: 'Logo',
  COVER: 'Cover image',
  PROFILE_PHOTO: 'Profile photo (your face)',
  STORE_FRONT: 'Store front',
  COUNTER: 'Counter',
  STORAGE: 'Storage area',
  GALLERY: 'Gallery',
  INTERIOR: 'Interior',
  EXTERIOR: 'Exterior',
  STAFF: 'Staff',
};

export const TERMS_VERSION = '2026-06-01';

// ============================================================================
// PAYOUT
// ============================================================================

/**
 * Where the vendor's earnings are sent.
 *
 * A discriminated union rather than one wide object with everything optional, because bank and
 * UPI share no fields and a shape that permits both lets the form post half of each.
 */
export type PayoutRequest =
  | {
      method: 'BANK_ACCOUNT';
      account_holder_name: string;
      account_number: string;
      ifsc_code: string;
    }
  | { method: 'UPI'; upi_id: string };

/**
 * What comes back. The account number and UPI id are encrypted server-side and never returned,
 * so a vendor revisiting the step sees `****4821` and re-types the number to change it.
 */
export type PayoutView = {
  method: 'BANK_ACCOUNT' | 'UPI';
  account_holder_name: string | null;
  account_number_last4: string | null;
  ifsc_code: string | null;
  upi_id_display: string | null;
  verified_at: string | null;
  last_changed_at: string | null;
  change_count: number;
  /**
   * Server-computed. Never re-derive this from `last_changed_at` here: the window is a rolling
   * 30 days measured against the server clock, and a browser clock a day out would either hide
   * an edit the vendor is entitled to or offer one the server is going to refuse.
   */
  can_edit: boolean;
  next_editable_at: string | null;
  cooldown_days: number;
};


export type DraftSummary = {
  draft_id: string;
  vendor_profile_id: string;
  current_step: OnboardingStep;
  module_types: VendorModuleType[];
  basic_info_completed: boolean;
  address_completed: boolean;
  payout_completed: boolean;
  payout_skipped: boolean;
  terms_completed: boolean;
  submitted_at: string | null;
  /**
   * Counts of in-flight (pre-submit) and claimed uploads. Used by the rail
   * to mark "Documents & photos" done as soon as the first file lands.
   */
  kyc_doc_count: number;
  photo_count: number;
  /**
   * Per-module-detail completion. Only modules requiring details that the
   * vendor picked at /start show up here. `true` means the corresponding
   * profile row exists. Use this for module-step rail tick marks.
   */
  module_details_completed: Partial<Record<VendorModuleType, boolean>>;
};

export type SavedBasicInfo = {
  business_name: string;
  owner_name: string;
  email_for_business: string;
  website_url: string | null;
  description: string | null;
  gst_number: string | null;
  business_registration_number: string | null;
  is_24x7: boolean;
};

export type SavedAddress = {
  address: string;
  landmark: string | null;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
};

export type StartRequest = { module_types: VendorModuleType[] };

export type BasicInfoRequest = {
  business_name: string;
  owner_name: string;
  email_for_business: string;
  website_url?: string;
  description?: string;
  gst_number?: string;
  business_registration_number?: string;
  is_24x7: boolean;
};

export type AddressRequest = {
  address: string;
  landmark?: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
};

export type TermsRequest = {
  terms_version: string;
  accept: true;
};

export type SubmitResponse = {
  vendor_profile_id: string;
  kyc_status: KycStatus;
  submitted_at: string;
  kyc_docs_claimed: number;
  photos_claimed: number;
};

export type StepResponse = {
  draft_id: string;
  current_step: OnboardingStep;
  basic_info_completed: boolean;
  address_completed: boolean;
  payout_completed: boolean;
  payout_skipped: boolean;
  terms_completed: boolean;
};

export type KycUploadResponse = {
  id: string;
  type: KycDocumentType;
  s3_key: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
};

export type PhotoUploadResponse = {
  id: string;
  type: VendorPhotoType;
  s3_key: string;
  s3_key_medium: string | null;
  s3_key_thumb: string | null;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  display_order: number;
  uploaded_at: string;
};
